import { API_BASE_URL, TOKEN_STORAGE_KEY } from '../config/index.js';

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  register({ email, name, password }) {
    return this.request('/auth/register', { method: 'POST', body: { email, name, password } });
  }

  login({ email, password }) {
    return this.request('/auth/login', { method: 'POST', body: { email, password } });
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Users ----
  listUsers({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }

  // ---- Nominations ----
  listNominations({ limit = 100, offset = 0 } = {}) {
    return this.request(`/nominations?limit=${limit}&offset=${offset}`);
  }

  approveNomination(payload) {
    return this.request('/nominations', { method: 'POST', body: payload });
  }

  // Whether AI-assisted extraction (Gemini) is configured for this deployment.
  getExtractionStatus() {
    return this.request('/nominations/extraction-status');
  }

  // Advisory-only draft extraction from raw inbound text (email/WhatsApp).
  // Never writes the ledger — the result still goes through approveNomination().
  extractNomination(rawText) {
    return this.request('/nominations/extract', { method: 'POST', body: { rawText } });
  }

  // ---- Credit & Compliance ----
  listCreditAccounts() {
    return this.request('/credit/accounts');
  }

  evaluateCredit(payload) {
    return this.request('/credit/evaluate', { method: 'POST', body: payload });
  }

  releaseCreditHold(accountId, payload) {
    return this.request(`/credit/accounts/${accountId}/release`, { method: 'POST', body: payload });
  }

  // ---- Settings: organization & assets ----
  getEntityProfile() {
    return this.request('/settings/entity-profile');
  }

  updateEntityProfile(payload) {
    return this.request('/settings/entity-profile', { method: 'PUT', body: payload });
  }

  listLocations() {
    return this.request('/settings/locations');
  }

  createLocation(payload) {
    return this.request('/settings/locations', { method: 'POST', body: payload });
  }

  deleteLocation(id) {
    return this.request(`/settings/locations/${id}`, { method: 'DELETE' });
  }

  listBarges() {
    return this.request('/settings/barges');
  }

  createBarge(payload) {
    return this.request('/settings/barges', { method: 'POST', body: payload });
  }

  getCompatibilityMatrix() {
    return this.request('/settings/compatibility-matrix');
  }

  publishCompatibilityMatrix(payload) {
    return this.request('/settings/compatibility-matrix', { method: 'POST', body: payload });
  }

  getHorizons() {
    return this.request('/settings/horizons');
  }

  updateHorizons(payload) {
    return this.request('/settings/horizons', { method: 'PUT', body: payload });
  }

  // ---- Scheduling ----
  getSchedulingBoard() {
    return this.request('/scheduling/board');
  }

  evaluateAssignment(payload) {
    return this.request('/scheduling/evaluate', { method: 'POST', body: payload });
  }

  assignStem(payload) {
    return this.request('/scheduling/assign', { method: 'POST', body: payload });
  }

  overrideScheduling(payload) {
    return this.request('/scheduling/override', { method: 'POST', body: payload });
  }

  // ---- Execution (barge master) ----
  getExecutionSession() {
    return this.request('/execution/session');
  }

  checkContinuity(payload) {
    return this.request('/execution/continuity-check', { method: 'POST', body: payload });
  }

  sealDelivery(payload) {
    return this.request('/execution/seal', { method: 'POST', body: payload });
  }

  // ---- Invoicing & ERP ----
  getInvoiceQueue() {
    return this.request('/invoicing/queue');
  }

  getInvoice(stemId) {
    return this.request(`/invoicing/invoice/${stemId}`);
  }

  postInvoice(payload) {
    return this.request('/invoicing/post', { method: 'POST', body: payload });
  }

  retryInvoice(id) {
    return this.request(`/invoicing/retry/${id}`, { method: 'POST' });
  }

  // ---- Claims & quality ----
  listClaims() {
    return this.request('/claims');
  }

  auditQuality(payload) {
    return this.request('/claims/audit', { method: 'POST', body: payload });
  }

  getLabExtractionStatus() {
    return this.request('/claims/lab-extraction-status');
  }

  extractLabPdf(payload) {
    return this.request('/claims/lab-extract', { method: 'POST', body: payload });
  }

  exportClaimBundle(payload) {
    return this.request('/claims/export', { method: 'POST', body: payload });
  }

  transitionClaim(code, payload) {
    return this.request(`/claims/${encodeURIComponent(code)}/transition`, { method: 'POST', body: payload });
  }

  // ---- Quality Pack ----
  getQualityPackEditions() { return this.request('/quality-pack/editions'); }

  // ---- Buyer (customer) portal ----
  getMyBuyerOrders() {
    return this.request('/portal/my-orders');
  }

  // Public: is the buyer portal currently accepting customer sign-ins?
  getPortalStatus() {
    return this.request('/portal/status');
  }

  getMyBuyerClaims() {
    return this.request('/portal/my-claims');
  }

  raiseBuyerClaim(payload) {
    return this.request('/portal/my-claims', { method: 'POST', body: payload });
  }

  // Delivery receipt (BDN) is plain text — fetch with auth and return the body
  // so the page can trigger a browser download.
  async downloadBuyerReceipt(orderId) {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/portal/my-orders/${orderId}/receipt`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => null);
      throw new Error(payload?.error?.message || `Download failed (${res.status})`);
    }
    return res.text();
  }

  // ---- Rules packs ----
  getRulesPacks() {
    return this.request('/rules-packs');
  }

  verifyStagePack(payload) {
    return this.request('/rules-packs/verify-stage', { method: 'POST', body: payload });
  }

  impactPreview(payload) {
    return this.request('/rules-packs/impact-preview', { method: 'POST', body: payload });
  }

  proposeActivation(payload) {
    return this.request('/rules-packs/propose-activation', { method: 'POST', body: payload });
  }

  // ---- Integrations ----
  listIntegrations() {
    return this.request('/integrations');
  }

  getIntegrationMappings() {
    return this.request('/integrations/mappings');
  }

  testIntegration(id) {
    return this.request(`/integrations/${id}/test`, { method: 'POST' });
  }

  toggleIntegration(id, enabled) {
    return this.request(`/integrations/${id}/toggle`, { method: 'PATCH', body: { enabled } });
  }

  publishMapping(payload) {
    return this.request('/integrations/mappings/publish', { method: 'POST', body: payload });
  }

  // ---- Operational parameters & notifications ----
  listOpParameters() {
    return this.request('/ops/parameters');
  }

  proposeParameter(id, payload) {
    return this.request(`/ops/parameters/${id}/propose`, { method: 'POST', body: payload });
  }

  getNotificationRoutes() {
    return this.request('/ops/notifications/routes');
  }

  testSendAlert(payload) {
    return this.request('/ops/notifications/test-send', { method: 'POST', body: payload });
  }

  // ---- Buyer portal ----
  getPortalState() {
    return this.request('/portal');
  }

  setPortalMaster(payload) {
    return this.request('/portal/master', { method: 'POST', body: payload });
  }

  toggleCounterparty(id, enabled) {
    return this.request(`/portal/counterparties/${id}/toggle`, { method: 'PATCH', body: { enabled } });
  }

  // ---- Dual-approval co-signing ----
  getPendingApprovals() {
    return this.request('/approvals/pending');
  }

  cosignApproval(id, pin) {
    return this.request(`/approvals/${id}/cosign`, { method: 'POST', body: { pin } });
  }
}

export default new ApiService();
